import React, { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useState } from 'react'

import { useNavigationVariant } from '../hooks/useNavigationVariant'
import { NavigationVariant } from '../types/navigationVariant'

interface ContextState {
  closeNavigation: () => void
  isInverted: boolean
  isNavigationOpen: boolean
  openNavigation: () => void
  setShouldCloseDropdown: (arg: boolean) => void
  shouldCloseDropdown: boolean
  variant: NavigationVariant
}

const NavigationContext = createContext<ContextState | undefined>(undefined)

interface NavigationContextProps {
  children?: ReactNode
  umbrellaId?: string
}

export const NavigationContextProvider = ({ children, umbrellaId }: NavigationContextProps): ReactElement | null => {
  const [isNavigationOpen, setIsNavigationOpen] = useState(false)
  const [shouldCloseDropdown, setShouldCloseDropdown] = useState(false)
  const variant = useNavigationVariant({ umbrellaId })
  const isInverted = variant === 'inverted'

  const closeNavigation = useCallback(() => setIsNavigationOpen(false), [])
  const openNavigation = useCallback(() => setIsNavigationOpen(true), [])

  useEffect(() => {
    if (shouldCloseDropdown) {
      setShouldCloseDropdown(false)
    }
  }, [shouldCloseDropdown])

  return (
    <NavigationContext.Provider
      value={{
        closeNavigation,
        isInverted,
        isNavigationOpen,
        openNavigation,
        setShouldCloseDropdown,
        shouldCloseDropdown,
        variant,
      }}
    >
      {children}
    </NavigationContext.Provider>
  )
}

export const useNavigation = () => {
  const context = useContext(NavigationContext)

  if (!context) {
    throw new Error('NavigationContext is missing in the module!')
  }

  return context
}
